/**
 * Active QueryClient handle — a non-React accessor to the QueryClient owned by
 * the mounted `SDKProvider`.
 *
 * The SDK owns the only QueryClient in the app. Most consumers reach it through
 * React context (`QueryClientProvider`), but devtool / engineering surfaces run
 * OUTSIDE the provider subtree (the DevToolkit mounts at the app root) yet still
 * need to read or write the live cache. `SDKProvider` registers its client here
 * on mount and clears it on unmount, so those surfaces can resolve it without a
 * React context. Returns `undefined` when no provider is mounted (e.g. demo-mode
 * routes that render without `SDKProvider`) — callers must handle that and no-op.
 *
 * Pinned on `globalThis` (NOT a module-level variable) because the SDK ships
 * multiple entry-point bundles (`/client`, `/devtool`, …). `SDKProvider` lives
 * in the `/client` bundle while `getActiveQueryClient` is re-exported from the
 * code-split `/devtool` bundle; a module-level singleton would be duplicated
 * across those graphs, so the setter and getter would touch different copies.
 * The global pin matches the SDK's other cross-bundle singletons (NATS client,
 * spec registry, perf counters).
 */

import type { QueryClient } from '@tanstack/react-query';

declare global {
  // eslint-disable-next-line no-var
  var __morfi_active_query_client__: QueryClient | undefined;
}

/** Register (or clear, with `undefined`) the provider's QueryClient. */
export function setActiveQueryClient(qc: QueryClient | undefined): void {
  globalThis.__morfi_active_query_client__ = qc;
}

/** The QueryClient of the mounted SDKProvider, or `undefined` when none. */
export function getActiveQueryClient(): QueryClient | undefined {
  return globalThis.__morfi_active_query_client__;
}
